import React, { useState } from "react";

interface Props {
  lang: "es" | "en";
  /** International phone number (digits only, country code first). */
  phone?: string;
}

export function WhatsAppFab({ lang, phone }: Props) {
  const rawNum = (phone || (import.meta.env.VITE_WHATSAPP_NUMBER as string) || "18093763247").replace(/\D/g, "");
  const defaultMsg = lang === "es"
    ? "Hola NutriMarket, tengo una pregunta sobre un producto."
    : "Hi NutriMarket, I have a question about a product.";

  const [showPreview, setShowPreview] = useState(false);
  const [customMsg, setCustomMsg] = useState(defaultMsg);
  const [customNum, setCustomNum] = useState(rawNum);

  const href = `https://wa.me/${customNum.replace(/\D/g, "")}?text=${encodeURIComponent(customMsg)}`;

  const labels = {
    es: {
      chatBtn: "Chatea por WhatsApp",
      title: "¿Enviar mensaje por WhatsApp?",
      messageLabel: "Mensaje:",
      cancel: "Cancelar",
      send: "Enviar",
      to: "Para:",
    },
    en: {
      chatBtn: "Chat on WhatsApp",
      title: "Send message via WhatsApp?",
      messageLabel: "Message:",
      cancel: "Cancel",
      send: "Send",
      to: "To:",
    },
  };

  const t = labels[lang];

  return (
    <>
      <button
        onClick={() => setShowPreview(true)}
        aria-label={t.chatBtn}
        data-testid="whatsapp-fab"
        style={{
          position: "fixed",
          right: 16,
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "#25D366",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 6px 18px rgba(37,211,102,.45)",
          zIndex: 90,
          border: "none",
          cursor: "pointer",
        }}
      >
        <svg width="26" height="26" viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
          <path d="M16 .5C7.4.5.5 7.4.5 16c0 2.8.7 5.5 2.1 7.9L.5 31.5l7.8-2c2.3 1.3 4.9 2 7.7 2 8.6 0 15.5-6.9 15.5-15.5S24.6.5 16 .5zm0 28.2c-2.5 0-4.9-.7-7-2l-.5-.3-4.6 1.2 1.2-4.5-.3-.5c-1.4-2.2-2.2-4.7-2.2-7.3C2.6 8.6 8.6 2.6 16 2.6S29.4 8.6 29.4 16 23.4 28.7 16 28.7zm7.4-9.9c-.4-.2-2.4-1.2-2.8-1.3-.4-.1-.6-.2-.9.2-.3.4-1 1.3-1.3 1.6-.2.3-.5.3-.9.1-.4-.2-1.7-.6-3.3-2-1.2-1.1-2-2.4-2.3-2.8-.2-.4 0-.6.2-.8.2-.2.4-.5.6-.7.2-.2.3-.4.4-.7.1-.3.1-.5 0-.7-.1-.2-.9-2.1-1.2-2.9-.3-.8-.6-.7-.9-.7h-.7c-.2 0-.6.1-.9.4-.3.4-1.2 1.2-1.2 2.8 0 1.7 1.2 3.3 1.4 3.5.2.2 2.4 3.7 5.9 5.2.8.4 1.5.6 2 .7.8.3 1.5.2 2.1.1.6-.1 2-.8 2.3-1.6.3-.8.3-1.4.2-1.6-.1-.1-.4-.2-.7-.4z"/>
        </svg>
      </button>

      {showPreview && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setShowPreview(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 360,
              width: "100%",
              padding: 24,
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#25D366",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 32 32" fill="#fff">
                  <path d="M16 .5C7.4.5.5 7.4.5 16c0 2.8.7 5.5 2.1 7.9L.5 31.5l7.8-2c2.3 1.3 4.9 2 7.7 2 8.6 0 15.5-6.9 15.5-15.5S24.6.5 16 .5zm0 28.2c-2.5 0-4.9-.7-7-2l-.5-.3-4.6 1.2 1.2-4.5-.3-.5c-1.4-2.2-2.2-4.7-2.2-7.3C2.6 8.6 8.6 2.6 16 2.6S29.4 8.6 29.4 16 23.4 28.7 16 28.7zm7.4-9.9c-.4-.2-2.4-1.2-2.8-1.3-.4-.1-.6-.2-.9.2-.3.4-1 1.3-1.3 1.6-.2.3-.5.3-.9.1-.4-.2-1.7-.6-3.3-2-1.2-1.1-2-2.4-2.3-2.8-.2-.4 0-.6.2-.8.2-.2.4-.5.6-.7.2-.2.3-.4.4-.7.1-.3.1-.5 0-.7-.1-.2-.9-2.1-1.2-2.9-.3-.8-.6-.7-.9-.7h-.7c-.2 0-.6.1-.9.4-.3.4-1.2 1.2-1.2 2.8 0 1.7 1.2 3.3 1.4 3.5.2.2 2.4 3.7 5.9 5.2.8.4 1.5.6 2 .7.8.3 1.5.2 2.1.1.6-.1 2-.8 2.3-1.6.3-.8.3-1.4.2-1.6-.1-.1-.4-.2-.7-.4z"/>
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "#111" }}>{t.title}</h3>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 4, fontWeight: 500 }}>{t.to}</div>
              <input
                type="tel"
                value={(() => {
                  const m = customNum.replace(/\D/g, "").match(/^(\d)(\d{3})(\d{3})(\d{4})$/);
                  return m ? `+${m[1]}-${m[2]}-${m[3]}-${m[4]}` : customNum;
                })()}
                onChange={(e) => setCustomNum(e.target.value.replace(/\D/g, ""))}
                style={{
                  width: "100%",
                  background: "#F0F2F5",
                  borderRadius: 12,
                  padding: "10px 14px",
                  fontSize: 15,
                  color: "#111",
                  border: "1px solid transparent",
                  outline: "none",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  fontWeight: 500,
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#25D366";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "transparent";
                }}
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: "#666", marginBottom: 6, fontWeight: 500 }}>{t.messageLabel}</div>
              <textarea
                value={customMsg}
                onChange={(e) => setCustomMsg(e.target.value)}
                rows={3}
                style={{
                  width: "100%",
                  background: "#F0F2F5",
                  borderRadius: 12,
                  padding: "14px 16px",
                  fontSize: 15,
                  color: "#111",
                  lineHeight: 1.5,
                  border: "1px solid transparent",
                  outline: "none",
                  resize: "vertical",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "#25D366";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "transparent";
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "1px solid #E0E0E0",
                  background: "#fff",
                  color: "#333",
                  fontSize: 15,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {t.cancel}
              </button>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setShowPreview(false)}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#25D366",
                  color: "#fff",
                  fontSize: 15,
                  fontWeight: 600,
                  textAlign: "center",
                  textDecoration: "none",
                  display: "inline-block",
                  cursor: "pointer",
                }}
              >
                {t.send}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
